import * as React from 'react';
import * as yup from 'yup';
import {format, getDate, parse} from 'date-fns';
import {FormProvider, useForm} from 'react-hook-form';
import {useRef} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';

import Button from '../../ui/Buttons/Button';
import DatePicker from '../../ui/forms/DatePicker/DatePicker';
import {Alerts} from '../../ui/Alerts/Alerts';
import {AutoRoEnrollment, IntervalType, UpdateAutoRoItemParams} from '../AutoReorderClass';
import {ContentModal} from '../../ui/modals/ContentModal/ContentModal';
import {dateObjectToString} from '../../tools/date.functions';
import {ItemCards} from '../../items/ItemCard/ItemCards';
import {Select} from '../../ui/forms/Select/Select';
import {Stepper} from '../../ui/forms/Stepper/Stepper';
import {TemplateHelpers} from '../../tools/template-helpers.class';
import {User} from '../../users/user.class';

interface EnrollAutoReorderForm {
    interval: string;
    nextShipDate: string;
    units: number;
}

interface EnrollAutoReorderModalProps {
    autoRoEnrollment: AutoRoEnrollment;
    onHide: () => void;
    onSubmit: (updateAutoRoItemParams: UpdateAutoRoItemParams) => void;
    show: boolean;
    showPkgQtyWarning?: boolean;
    user: User;
}

export const EnrollAutoReorderModal = ({
    autoRoEnrollment,
    onHide,
    onSubmit,
    show,
    showPkgQtyWarning,
    user,
}: EnrollAutoReorderModalProps) => {
    const componentName = `EnrollAutoReorderModal`;
    const formRef = useRef(null);
    const tomorrow = Date.now() + 86400000;
    const today = format(tomorrow, `MM/dd/yyyy`);
    const formValidation = yup.object().shape({
        nextShipDate: yup
            .date()
            .typeError(`Invalid Date`)
            .min(today, `Date cannot be today or in the past`)
            .required(`Start date is required`),
        units: yup
            .number()
            .typeError(`Invalid Quantity`)
            .required(`Quantity is required`)
            .min(1, `You must specify at least 1 unit`)
            .test(`units`, `Quantity must not exceed max order qty`, (units) => {
                return autoRoEnrollment?.qtyLimit ? units <= autoRoEnrollment.qtyLimit : true;
            })
            .max(1000000, `You must specify no more than 1000000 units`),
    });
    const useFormReturn = useForm<EnrollAutoReorderForm>({
        resolver: yupResolver(formValidation),
    });
    const formValues = useFormReturn.watch();

    /**
     * Returns desired verbiage for "day of month" re-occurrence of auto-reorder
     * @param isValid
     * @param dateString
     * @param interval
     */
    const getAutoReorderIntervalMessage = (isValid: boolean, dateString: string, interval: string): JSX.Element => {
        // Prevent messaging from display if not valid
        if (!isValid || !dateString) {
            return <></>;
        }

        // Else determine appropriate message based on interval
        const dayOfMonth = getDate(parse(dateString, `MM/dd/yyyy`, new Date()));
        if (interval === `1W`) {
            return (
                <>
                    Order will be created every <strong>week</strong> starting <strong>{TemplateHelpers.formatDate(dateString)}</strong>
                </>
            );
        }
        if (interval === `1M`) {
            return (
                <>
                    Order will be created on <strong>{TemplateHelpers.formatDate(dateString)}</strong> and re-occur on{' '}
                    <strong>{`day ${dayOfMonth}${dayOfMonth > 28 ? ' (or the last day)' : ''}`}</strong> of each month.
                </>
            );
        }
        if (interval[1] === `W`) {
            return (
                <>
                    Order will be created every <strong>{interval[0]} weeks</strong> starting{' '}
                    <strong>{TemplateHelpers.formatDate(dateString)}</strong>
                </>
            );
        }
        if (interval[1] === `M`) {
            return (
                <>
                    Order will be created on{' '}
                    <strong>
                        day {`${dayOfMonth}${dayOfMonth > 28 ? ' (or the last day)' : ''}`} every {getMonthCount(interval[0])} months
                    </strong>{' '}
                    starting {TemplateHelpers.formatDate(dateString)}
                </>
            );
        }
    };

    /**
     * Provides drop down interval options for form
     */
    const getIntervalOptions = () => {
        // Build base options
        const options = [
            {key: `1W`, value: `1 week`},
            {key: `2W`, value: `2 weeks`},
            {key: `3W`, value: `3 weeks`},
            {key: `1M`, value: `Month`},
            {key: `2M`, value: `2 Months`},
            {key: `3M`, value: `3 Months`},
        ];

        // Add custom interval option if necessary
        if (
            autoRoEnrollment?.intervalNum &&
            autoRoEnrollment?.intervalNum !== 1 &&
            autoRoEnrollment?.intervalNum !== 2 &&
            autoRoEnrollment?.intervalNum !== 3
        ) {
            options.push({
                key: `${autoRoEnrollment?.intervalNum}${autoRoEnrollment?.intervalType}`,
                value: `${autoRoEnrollment?.intervalNum} ${autoRoEnrollment?.intervalType === 'M' ? 'Months' : 'Weeks'}`,
            });
        }
        // Return all interval options
        return options;
    };

    /**
     * Returns desired monthCount verbiage
     * @param monthCount
     */
    const getMonthCount = (monthCount: string) => {
        // Handle drop down values
        if (monthCount === `2`) {
            return `two`;
        }
        if (monthCount === `3`) {
            return `three`;
        }

        // Handle non drop down values
        return monthCount;
    };

    /**
     * Handles submission of enrollAutoReorderForm
     * @param enrollAutoReorderForm
     */
    const submitEnrollAutoReorderForm = (enrollAutoReorderForm: EnrollAutoReorderForm) => {
        onSubmit({
            active: true,
            intervalNum: parseInt(enrollAutoReorderForm.interval[0]),
            intervalType: enrollAutoReorderForm.interval[1] as IntervalType,
            item: autoRoEnrollment?.item.id,
            startDate: dateObjectToString(enrollAutoReorderForm.nextShipDate as any),
            status: `save`,
            units: enrollAutoReorderForm.units,
        });
    };

    /**
     * Submits form
     */
    const submitForm = () => {
        if (formRef.current) {
            formRef.current.dispatchEvent(new Event(`submit`, {bubbles: true, cancelable: true}));
        }
    };

    /**
     * Template
     */
    return (
        <ContentModal
            footer={
                <>
                    <Button
                        modal={true}
                        modalOrderChangeButton1={true}
                        onClick={onHide}
                        size="smToLg"
                        variant="outline-secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        e2e="enrollBtn"
                        modal={true}
                        modalOrderChangeButton2={true}
                        onClick={submitForm}
                        size="smToLg"
                        type="submit"
                        variant="secondary"
                    >
                        {autoRoEnrollment?.nextShipDate ? `Update Enrollment` : `Enroll`}
                    </Button>
                </>
            }
            onClose={onHide}
            show={show}
            title="Enrollment Details"
        >
            <FormProvider {...useFormReturn}>
                <form
                    noValidate={true}
                    onSubmit={(event) => {
                        useFormReturn
                            .handleSubmit(submitEnrollAutoReorderForm)(event)
                            .catch((handleSubmitErr) => {
                                // eslint-disable-next-line no-console
                                console.log(handleSubmitErr);
                            });
                    }}
                    ref={formRef}
                >
                    {autoRoEnrollment?.nextShipDate && (
                        <Alerts
                            message="This item is already enrolled in auto-reorder. Any edits made here will be applied to your next shipment."
                            variant="warning"
                        />
                    )}
                    {showPkgQtyWarning && (
                        <Alerts
                            message="Replacement pkg quantity is different than original pkg quantity"
                            variant="warning"
                        />
                    )}
                    <ItemCards
                        cardOptions={{
                            contextMenu: false,
                            featured: true,
                            hideAddToOrder: true,
                            hideCheckbox: true,
                            showLastPurchased: false,
                        }}
                        componentName={componentName}
                        gaItemListName={componentName}
                        items={autoRoEnrollment?.item ? [autoRoEnrollment.item] : []}
                        user={user}
                    />
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 -tw-mx-2.5 mt-2">
                        <div className="tw-w-full tw-px-2.5">
                            {autoRoEnrollment?.qtyLimit && <strong>Max Order Qty: {autoRoEnrollment.qtyLimit}</strong>}
                            <Stepper
                                className="[&_input]:!tw-h-[52px] [&_Button]:!tw-h-[52px] [&_Button]:!tw-w-[52px] tw-w-full tw-flex tw-mb-6 tw-mx-[-1px]"
                                large={true}
                                maximum={autoRoEnrollment?.qtyLimit || 1000000}
                                name="units"
                                onUpdate={(val) => {
                                    useFormReturn.setValue(`units`, val);
                                }}
                                value={autoRoEnrollment?.units}
                                {...useFormReturn.register(`units`)}
                            />
                            <Select
                                defaultValue={
                                    autoRoEnrollment ? `${autoRoEnrollment.intervalNum}${autoRoEnrollment.intervalType}` : undefined
                                }
                                id="autoReorderEnrollComponent__interval"
                                label="Schedule"
                                name="interval"
                                options={getIntervalOptions()}
                                {...useFormReturn.register(`interval`)}
                            />
                            <DatePicker
                                label={autoRoEnrollment?.nextShipDate ? `Next Shipment Date` : `Start Date`}
                                min={tomorrow}
                                name="nextShipDate"
                                value={TemplateHelpers.formatDate(autoRoEnrollment?.nextShipDate)}
                                {...useFormReturn.register(`nextShipDate`)}
                            />
                            <div>
                                {getAutoReorderIntervalMessage(
                                    useFormReturn.formState.isValid,
                                    formValues.nextShipDate,
                                    formValues.interval,
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </ContentModal>
    );
};
